import {
    ConvertWaitlistEntryDTO,
    CreateWaitlistEntryWithOptionsDTO,
    PromptWaitlistEntriesDTO,
    UpdateWaitlistEntryMerchantNotesDTO,
    WaitlistEntryAuditEntryDTO,
    WaitlistEntryCountDTO,
    WaitlistEntryDTO,
} from './DTO/WaitlistEntryDTO'
import { DTOAdapter } from './DTOAdapter'
import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable, map } from 'rxjs'
import { WaitlistEntry } from '@app/domain/WaitlistEntry'
import { WaitlistEntryAuditEntry } from '@app/domain/WaitlistEntryAuditEntry'
import { environment } from '@environments/environment'
import { DateTime } from 'luxon'

@Injectable({
    providedIn: 'root',
})
export class WaitlistService {

    constructor(
        private http: HttpClient,
        private dtoAdapter: DTOAdapter,
    ) { }

    getWaitlistEntries(businessId: string, venueId: string, date: Date): Observable<WaitlistEntry[]> {
        const path = `/organisation/business/${businessId}/venue/${venueId}/waitlist`
        const url = new URL(path, environment.apiBaseURL)
        const dateString = DateTime.fromJSDate(date, { zone: environment.assumedVenueTimeZone }).toISODate()!
        const params = new HttpParams().set('date', dateString)
        return this.http.get<WaitlistEntryDTO[]>(url.toString(), { params })
            .pipe(map(dtos => dtos.map(dto => this.dtoAdapter.adaptWaitlistEntryDto(dto))))
    }

    getWaitlistEntryCount(businessId: string, venueId: string, date: Date): Observable<number> {
        const path = `/organisation/business/${businessId}/venue/${venueId}/waitlist/count`
        const url = new URL(path, environment.apiBaseURL)
        const dateString = DateTime.fromJSDate(date, { zone: environment.assumedVenueTimeZone }).toISODate()!
        const params = new HttpParams().set('date', dateString)
        return this.http.get<WaitlistEntryCountDTO>(url.toString(), { params })
            .pipe(map(dto => dto.count))
    }

    getWaitlistEntry(businessId: string, venueId: string, entryId: string): Observable<WaitlistEntry> {
        const path = `/organisation/business/${businessId}/venue/${venueId}/waitlist/${entryId}`
        const url = new URL(path, environment.apiBaseURL)
        return this.http.get<WaitlistEntryDTO>(url.toString())
            .pipe(map(dto => this.dtoAdapter.adaptWaitlistEntryDto(dto)))
    }

    getWaitlistEntryAudit(businessId: string, venueId: string, entryId: string): Observable<WaitlistEntryAuditEntry[]> {
        const path = `/organisation/business/${businessId}/venue/${venueId}/waitlist/${entryId}/audit`
        const url = new URL(path, environment.apiBaseURL)
        return this.http.get<WaitlistEntryAuditEntryDTO[]>(url.toString())
            .pipe(map(dtos => dtos.map(dto => this.dtoAdapter.adaptWaitlistEntryAuditEntryDto(dto))))
    }

    createWaitlistEntry(businessId: string, venueId: string, body: CreateWaitlistEntryWithOptionsDTO): Observable<WaitlistEntry> {
        const path = `/organisation/business/${businessId}/venue/${venueId}/waitlist`
        const url = new URL(path, environment.apiBaseURL)
        return this.http.post<WaitlistEntryDTO>(url.toString(), body)
            .pipe(map(dto => this.dtoAdapter.adaptWaitlistEntryDto(dto)))
    }

    updateWaitlistEntryMerchantNotes(businessId: string, venueId: string, entryId: string, merchantNotes: string | null): Observable<WaitlistEntry> {
        const path = `/organisation/business/${businessId}/venue/${venueId}/waitlist/${entryId}/merchant-notes`
        const url = new URL(path, environment.apiBaseURL)
        const body = new UpdateWaitlistEntryMerchantNotesDTO(merchantNotes)
        return this.http.patch<WaitlistEntryDTO>(url.toString(), body)
            .pipe(map(dto => this.dtoAdapter.adaptWaitlistEntryDto(dto)))
    }

    promptWaitlistEntries(businessId: string, venueId: string, entryIds: string[]): Observable<void> {
        const path = `/organisation/business/${businessId}/venue/${venueId}/waitlist/prompt`
        const url = new URL(path, environment.apiBaseURL)
        const body = new PromptWaitlistEntriesDTO(entryIds)
        return this.http.post<void>(url.toString(), body)
    }

    deleteWaitlistEntry(businessId: string, venueId: string, entryId: string): Observable<void> {
        const path = `/organisation/business/${businessId}/venue/${venueId}/waitlist/${entryId}`
        const url = new URL(path, environment.apiBaseURL)
        return this.http.delete<void>(url.toString())
    }

    convertWaitlistEntry(businessId: string, venueId: string, entryId: string, tableIds: string[], areaId: string, durationMinutes: number): Observable<any> {
        const path = `/organisation/business/${businessId}/venue/${venueId}/waitlist/${entryId}/convert`
        const url = new URL(path, environment.apiBaseURL)
        const body = new ConvertWaitlistEntryDTO(tableIds, areaId, durationMinutes)
        return this.http.post(url.toString(), body)
    }
}
